import { DeleteOutlined, DoneAll, InsertDriveFileOutlined, Person } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Chip,
    Fade,
    IconButton,
    Link,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { format } from 'date-fns';

import useTranslate from 'hooks/useTranslate';
import type { ChatMessage, ChatReference } from 'types';

const DELETE_WINDOW_MS = 30 * 60 * 1000;

interface ChatMessageItemProps {
    message: ChatMessage;
    isOwn: boolean;
    currentUserInitial: string;
    onDelete: (message: ChatMessage) => void;
    onReferenceClick: (reference: ChatReference) => void;
}

const parseDate = (timestamp: string | null | undefined): Date | null => {
    if (!timestamp) {
        return null;
    }

    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
};

const formatMessageTime = (timestamp: string | null | undefined) => {
    const date = parseDate(timestamp);
    if (!date) {
        return '';
    }

    return format(date, 'h:mm a');
};

const DELETED_MESSAGE_LABELS = new Set(['deleted', 'eliminado', 'supprimé']);

const isDeletedMessage = (text: string, deletedLabel: string) => {
    const normalized = text.trim().toLowerCase();
    return (
        normalized === deletedLabel.trim().toLowerCase() || DELETED_MESSAGE_LABELS.has(normalized)
    );
};

export const ChatMessageItem = ({
    message,
    isOwn,
    currentUserInitial,
    onDelete,
    onReferenceClick,
}: ChatMessageItemProps) => {
    const { translate } = useTranslate();
    const isSystem = message.user_id === null;
    const deleted = isDeletedMessage(message.text ?? '', translate('deleted'));
    const createdAt = parseDate(message.created_at);
    const canDelete =
        isOwn &&
        !deleted &&
        Boolean(createdAt) &&
        Date.now() <= (createdAt as Date).getTime() + DELETE_WINDOW_MS;
    const author = isOwn ? translate('you') : message.user || translate('chat');
    const authorInitial = (message.user?.[0] || author[0] || '?').toUpperCase();

    if (isSystem) {
        return (
            <Fade in>
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.5 }}>
                    <Chip
                        size="small"
                        label={message.text}
                        sx={{
                            bgcolor: 'grey.100',
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                        }}
                    />
                </Box>
            </Fade>
        );
    }

    return (
        <Fade in>
            <Box
                data-message
                sx={{
                    display: 'flex',
                    justifyContent: isOwn ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-start',
                    gap: { xs: 0.75, sm: 1 },
                }}
            >
                {!isOwn ? (
                    <Avatar
                        sx={{
                            width: { xs: 32, sm: 36 },
                            height: { xs: 32, sm: 36 },
                            mt: 0.25,
                            bgcolor: 'primary.main',
                            fontSize: '0.8125rem',
                            fontWeight: 700,
                        }}
                    >
                        {authorInitial || <Person fontSize="small" />}
                    </Avatar>
                ) : null}

                <Stack
                    spacing={0.5}
                    sx={{
                        maxWidth: { xs: '82%', sm: '72%', md: '64%' },
                        alignItems: isOwn ? 'flex-end' : 'flex-start',
                        minWidth: 0,
                    }}
                >
                    {!isOwn ? (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ px: 0.75, fontWeight: 600 }}
                        >
                            {author}
                        </Typography>
                    ) : null}

                    <Stack
                        direction={isOwn ? 'row-reverse' : 'row'}
                        spacing={0.75}
                        sx={{ alignItems: 'flex-end', maxWidth: '100%' }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                px: { xs: 1.5, sm: 2 },
                                py: { xs: 1, sm: 1.25 },
                                bgcolor: deleted
                                    ? 'action.disabledBackground'
                                    : isOwn
                                      ? 'primary.main'
                                      : 'background.paper',
                                color: deleted
                                    ? 'text.secondary'
                                    : isOwn
                                      ? 'primary.contrastText'
                                      : 'text.primary',
                                borderRadius: 2.5,
                                borderTopRightRadius: isOwn ? 0.75 : 2.5,
                                borderTopLeftRadius: isOwn ? 2.5 : 0.75,
                                border: deleted || !isOwn ? '1px solid' : 'none',
                                borderColor: 'divider',
                                boxShadow:
                                    deleted || !isOwn
                                        ? 'none'
                                        : '0 8px 18px rgba(131, 83, 226, 0.22)',
                                maxWidth: '100%',
                                opacity: deleted ? 0.7 : 1,
                            }}
                        >
                            {message.references?.length ? (
                                <Stack spacing={0.5} sx={{ mb: 0.75 }}>
                                    {message.references.map((reference) => (
                                        <Link
                                            key={`${reference.type}-${reference.id}`}
                                            component="button"
                                            type="button"
                                            underline="hover"
                                            onClick={() => onReferenceClick(reference)}
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                color: deleted
                                                    ? 'text.disabled'
                                                    : isOwn
                                                      ? 'inherit'
                                                      : 'primary.main',
                                                fontSize: '0.8125rem',
                                                fontWeight: 600,
                                                textAlign: 'left',
                                                maxWidth: '100%',
                                            }}
                                        >
                                            <InsertDriveFileOutlined sx={{ fontSize: 16 }} />
                                            {`${translate('chatReferencePrefix')}: ${
                                                reference.name ||
                                                `${reference.type} #${reference.id}`
                                            }`}
                                        </Link>
                                    ))}
                                </Stack>
                            ) : null}

                            <Typography
                                variant="body2"
                                sx={{
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.55,
                                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                                    fontStyle: deleted ? 'italic' : 'normal',
                                    opacity: deleted ? 0.8 : 1,
                                }}
                            >
                                {message.text}
                            </Typography>
                        </Paper>

                        {canDelete ? (
                            <Tooltip title={translate('deleteMessage')}>
                                <IconButton
                                    size="small"
                                    aria-label={translate('deleteMessage')}
                                    onClick={() => onDelete(message)}
                                    sx={{
                                        mb: 0.25,
                                        color: 'text.secondary',
                                        width: 36,
                                        height: 36,
                                        opacity: { xs: 1, sm: 0 },
                                        transition: 'opacity 0.15s ease',
                                        '[data-message]:hover &': {
                                            opacity: 1,
                                        },
                                    }}
                                >
                                    <DeleteOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                    </Stack>

                    <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                            px: 0.75,
                            alignItems: 'center',
                            color: 'text.secondary',
                        }}
                    >
                        <Typography variant="caption" sx={{ fontSize: '0.6875rem', opacity: 0.75 }}>
                            {formatMessageTime(message.created_at)}
                        </Typography>
                        {isOwn ? (
                            <DoneAll
                                sx={{
                                    fontSize: 14,
                                    color: message.read ? 'primary.main' : 'text.disabled',
                                }}
                            />
                        ) : null}
                    </Stack>
                </Stack>

                {isOwn ? (
                    <Avatar
                        sx={{
                            width: { xs: 32, sm: 36 },
                            height: { xs: 32, sm: 36 },
                            mt: 0.25,
                            bgcolor: 'grey.700',
                            fontSize: '0.8125rem',
                            fontWeight: 700,
                        }}
                    >
                        {currentUserInitial}
                    </Avatar>
                ) : null}
            </Box>
        </Fade>
    );
};
